import React from "react";

import { Card, Col, Form, Row } from "react-bootstrap";

import ContactFormReferenceSection from "@/components/contact/elements/ContactFormReference.component";
import { yesNoOptions } from "@/context/ContactProvider.context";
import { getCurrentLang } from "@/utils/Local";
import { formatFormOptionValue } from "@/utils/Toolbox";

import useContactContext from "../../../hooks/useContactContext";
import i18n from "../../../i18n";
import ContactFormLocationSection from "../elements/ContactFormLocationSection.component";
import ContactFormPhoneSection from "../elements/ContactFormPhoneSection.component";
import ContactFormProjectInfoSection from "../elements/ContactFormProjectInfoSection.component";

const ContactFormBuilders = () => {
  const { t } = i18n;
  const { onSubmit, methods, submitting } = useContactContext();
  methods.watch("hasSpecificProject");

  return (
    <Form className="w-100" onSubmit={methods.handleSubmit(onSubmit)}>
      <fieldset disabled={submitting}>
        <input
          type="hidden"
          {...methods.register("kind")}
          label={null}
          value="builders"
        />
        <input
          type="hidden"
          {...methods.register("lang")}
          label={null}
          value={getCurrentLang()}
        />
        <Card>
          <Card.Body>
            <Row>
              <Col xs={12}>
                <Card.Title>{t("Primary Information")}</Card.Title>
              </Col>

              <Col xs={12}>
                <Row>
                  <Col lg={6} xs={12}>
                    <Form.Group className="mb-3" controlId="name">
                      <Form.Label>{t("Full name")}</Form.Label>
                      <Form.Control
                        type="text"
                        {...methods.register("name")}
                        isInvalid={!!methods.formState.errors.name}
                      />
                      <Form.Control.Feedback type="invalid">
                        {t(methods.formState.errors.name?.message)}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  <Col lg={6} xs={12}>
                    <Form.Group className="mb-3" controlId="company">
                      <Form.Label>{t("Company")}</Form.Label>
                      <Form.Control
                        type="text"
                        {...methods.register("company")}
                        isInvalid={!!methods.formState.errors.company}
                      />
                      <Form.Control.Feedback type="invalid">
                        {t(methods.formState.errors.company?.message)}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  <Col lg={6} xs={12}>
                    <Form.Group className="mb-3" controlId="email">
                      <Form.Label>{t("Email")}</Form.Label>
                      <Form.Control
                        type="email"
                        {...methods.register("email")}
                        isInvalid={!!methods.formState.errors.email}
                      />
                      <Form.Control.Feedback type="invalid">
                        {t(methods.formState.errors.email?.message)}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  <Col lg={6} xs={12}>
                    <ContactFormPhoneSection
                      control={methods.control}
                      errors={methods.formState.errors}
                    />
                  </Col>
                </Row>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        <Card className="mt-4">
          <Card.Body>
            <Row>
              <Col xs={12}>
                <ContactFormLocationSection methods={methods} />
              </Col>
            </Row>
          </Card.Body>
        </Card>

        <Card className="mt-4">
          <Card.Body>
            <Row>
              <Col lg={12} xs={12}>
                <Form.Group
                  className="mb-3"
                  controlId="project-requires-financing"
                >
                  <Form.Label>
                    {t("Do you have a specific project in mind?")}
                  </Form.Label>
                  {yesNoOptions.map((v) => (
                    <Form.Check
                      key={`k-${v}`}
                      className="radio-pill"
                      id={`hasSpecificProject-${v}`}
                      label={t(formatFormOptionValue(v))}
                      type="radio"
                      value={v}
                      {...methods.register("hasSpecificProject", {
                        onChange: () => {
                          if (v === "yes") {
                            methods.setValue("note", undefined, {
                              shouldValidate: true,
                            });
                          } else {
                            methods.setValue("projectInfo", undefined, {
                              shouldValidate: true,
                            });
                          }
                          methods.trigger([
                            "hasSpecificProject",
                            "projectInfo",
                            "note",
                          ]);
                        },
                      })}
                      isInvalid={!!methods.formState.errors.hasSpecificProject}
                      name="hasSpecificProject"
                    />
                  ))}
                  <Form.Control.Feedback className="d-block" type="invalid">
                    {t(methods.formState.errors.hasSpecificProject?.message)}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {methods.getValues("hasSpecificProject") === "yes" && (
          <ContactFormProjectInfoSection methods={methods} />
        )}

        {methods.getValues("hasSpecificProject") === "no" && (
          <Card className="mt-4">
            <Card.Body>
              <Row>
                <Col xs={12}>
                  <Form.Group className="mb-3" controlId="note">
                    <Form.Label>
                      {t(
                        "Tell us briefly about your company and areas of interest",
                      )}
                    </Form.Label>
                    <Form.Control
                      as="textarea"
                      type="text"
                      {...methods.register("note")}
                      isInvalid={!!methods.formState.errors.note}
                    />
                    <Form.Control.Feedback type="invalid">
                      {t(methods.formState.errors.note?.message)}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        )}
        <ContactFormReferenceSection methods={methods} />
      </fieldset>
    </Form>
  );
};

export default ContactFormBuilders;
